import { MIN_SAFE_INTEGER } from 'Components/form/utils';

// Pure helpers extracted from useFormWrapper (CounterForm.tsx) so the price
// validation logic can be exhaustively unit-tested without standing up the
// whole AntD form, easy-peasy store, filters context, and live-pricing query.
//
// Scope: the floor that drives the inline "Must be at least X" error for the
// counter-bid `price` and `autobid_price` fields. Editing-aware exceptions
// (allow same as user's prior bid) are intentionally excluded from these
// helpers — current tests do not cover that flow.

export type DisplayedNextpriceInput = {
  fixedIndex: boolean;
  pricingNextVal: number | undefined;
  offset: number | undefined;
  nextpriceVal: number | null | undefined;
  nextpriceValFixed: number | null | undefined;
};

// Mirror of the PriceTicker "Minimum next bid" hint (Data.tsx Price component
// uses `val_fixed ?? val` for fixed mode). Fixed mode does NOT fall through
// to pricingNextVal — the /tenderoffer-pricing endpoint returns the index
// value (e.g. a -0.0025 modifier) even when called with fixed_index=true,
// and leaking that into the floor would pull validation well below what the
// user sees in the blue box.
export function computeDisplayedNextprice({
  fixedIndex,
  pricingNextVal,
  offset,
  nextpriceVal,
  nextpriceValFixed,
}: DisplayedNextpriceInput): number | undefined {
  const v = fixedIndex
    ? (nextpriceValFixed ?? offset ?? nextpriceVal)
    : (pricingNextVal ?? offset ?? nextpriceVal);
  return v ?? undefined;
}

export type OfferFloorInput = {
  displayedNextprice: number | undefined;
  fixedIndex: boolean;
  floor: number;
  // NUI-4538: bidder's existing-bid price re-quoted at the requested
  // warehouse (from /pricing). When set, it lets the user resubmit at their
  // unchanged effective price (e.g. volume-only edit) without being forced
  // above Min next bid. BE omits the field for non-editing bidders.
  equivalentVal?: number | undefined;
};

// Fall back to 0 in fixed mode instead of `floor` (which can be
// MIN_SAFE_INTEGER for Modifier-index tenders) — a fixed bid is an absolute
// price and must not be negative even if the underlying index allows
// negative modifiers in index mode.
export function computeOfferFloor({
  displayedNextprice,
  fixedIndex,
  floor,
  equivalentVal,
}: OfferFloorInput): number {
  if (equivalentVal != null && Number.isFinite(equivalentVal)) {
    return equivalentVal;
  }
  return displayedNextprice ?? (fixedIndex ? 0 : floor);
}

// Mirrors the validator inside priceRules. Returns the error key when the
// floor is real and the value falls below it, otherwise null. Skips when
// minprice is MIN_SAFE_INTEGER (Modifier-index "no real lower bound" sentinel)
// so Modifier-index bids can legitimately go negative in index mode.
export function validatePriceFloor(
  value: unknown,
  minprice: number | undefined
): 'below-min' | null {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  if (
    minprice !== undefined &&
    Number.isFinite(minprice) &&
    minprice > MIN_SAFE_INTEGER &&
    num < minprice
  ) {
    return 'below-min';
  }
  return null;
}
